// @flow
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { animated } from 'react-spring/web.cjs';
import { SPACING, FONT_WEIGHTS, COLORS, typography } from '@tablekit/theme';
import Button from '@tablekit/button';
import { margin } from '@tablekit/utils';
import Icon from '@tablekit/icon';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { getPageLocale } from 'components/PageContext';
import LoadingSpan from 'components/LoadingSpan';
import type { BlokComponentPropsType } from 'components/types';
import BaseCard, { FULL_TOP_IMAGE_CLASS, HOVER_SCALE } from 'styled/Card';
import type { CmsShopType } from './types';
import FooterContent from './Footer';
import { gridElementStyles, CardSeparator } from './styled';
import generateShopCardUrls from './utils/generateShopCardUrls';

type PropsType = BlokComponentPropsType<CmsShopType> & {
  defaultReserveLabel: string,
  style: any,
  utmSource: string
};

const Card = animated(styled(BaseCard)`
  > * {
    background-color: yellow;
  }

  ${gridElementStyles};
  & a {
    &,
    &:visited,
    &:active,
    &:hover {
      text-decoration: none !important;
    }
  }

  &:hover ${CardSeparator} {
    transform: scale(${HOVER_SCALE});
  }
`);

const ImageContainer = styled.a`
  height: 210px;
  width: 100%;
  position: relative;
  overflow: hidden;
  display: block;
  ${({ isLoading, isError }) => {
    if (isLoading) {
      return css`
        &:after {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: ${COLORS.WHITE_TRANSLUCENT.L3};
          z-index: 2;
        }
      `;
    }
    if (isError) {
      return css`
        background: ${COLORS.GRAY.L5};
      `;
    }
    return '';
  }}
`;

const Body = styled.div`
  min-height: 210px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1 1 0%;
`;

const LinkedBody = styled.a`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1 1 100%;
  color: ${COLORS.GRAY_TRANSLUCENT.L9};
  padding: ${SPACING.L4} ${SPACING.L4} 0;
`;

const ShopName = styled.div`
  ${typography('L5')};
  font-weight: ${FONT_WEIGHTS.SEMI_BOLD};
`;

const ShopNameSpan = styled(LoadingSpan)`
  min-width: 112px;
`;

const ImageFallback = styled(ShopName)`
  color: white;
  background: ${COLORS.PURPLE_TRANSLUCENT.L7};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const ShopBlurb = styled.div`
  ${typography('L3')};
  margin-bottom: ${SPACING.L5};
`;

const ShopBlurbSpan = styled(LoadingSpan)`
  min-width: 100%;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding: 0 ${SPACING.L4} ${SPACING.L4};
`;

const MenuItemButton = styled(Button)`
  flex: 1 1 0%;
  ${margin({ right: `${SPACING.L5} !important` })}
`;

const ShopCardFallback = ({
  style,
  utmSource: unformattedUtmSource,
  defaultReserveLabel,
  blok: {
    slug,
    tags,
    menu_name: menuButtonName,
    // $FlowFixMe Flow claims "menu_list" is missing in object type
    menu_list: menuListId
  }
}: PropsType) => {
  const locale = getPageLocale();
  const shopName = slug
    .split('-')
    .map((string = '') => string.slice(0, 1).toUpperCase() + string.slice(1))
    .join(' ');

  const utmSource = unformattedUtmSource
    .toLowerCase()
    .replace(/[^a-z0-9_]/gi, '_');

  const { shopUrlWithUtm, bookingUrlWithUtm } = generateShopCardUrls({
    locale,
    menuListId,
    utmSource,
    slug
  });

  return (
    <Card
      useLinkAnimation
      style={style}
      onMouseDown={(event) => {
        // if in storyblok editor prevent clicks
        if (window.storyblok) {
          event.stopPropagation();
          event.preventDefault();
        }
      }}
      data-testid="ShopCardFallback"
    >
      <ImageContainer
        href={shopUrlWithUtm}
        className={`${FULL_TOP_IMAGE_CLASS} TcShopLink`}
      >
        <ImageFallback>{shopName}</ImageFallback>
      </ImageContainer>
      <Body>
        <LinkedBody href={shopUrlWithUtm} className="TcShopLink">
          <ShopName>
            <ShopNameSpan>{shopName}</ShopNameSpan>
          </ShopName>
          <ShopBlurb>
            <ShopBlurbSpan>
              <p>
                <b>「注意」</b>
                この店はdinerAPIのデータがありません。Storyblokと「stagingーsnap」のみに表示されます!
              </p>
              <br />
              <p>
                <b>Warning:</b> This shop has no dinerAPI data and will only be
                shown in Storyblok or staging-snap!
              </p>
            </ShopBlurbSpan>
          </ShopBlurb>
        </LinkedBody>
        <ActionsRow>
          <MenuItemButton
            iconAfter={<Icon icon={faChevronRight} />}
            href={bookingUrlWithUtm}
            className="TcReserveLink"
          >
            {menuButtonName || defaultReserveLabel}
          </MenuItemButton>
        </ActionsRow>
      </Body>
      <FooterContent href={shopUrlWithUtm} tags={tags} />
    </Card>
  );
};

export default ShopCardFallback;
